import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import env from 'env';

import { postKnowledgebase, type BedrockApiResponse, rateKnowledgebase, type PostKnowledgebasePayload } from 'queries/common/postKnowledgebase';
import { RootStore } from 'store';
import { Toast } from '../toast';
import { parseErrors } from '../parseResponseErrors';
import { useSuggestions } from './useSuggestions';

export type ChatMessage = {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  sourceLinks?: Array<{ title: string; url: string }>;
  log_id: string,
  action?: {
    type?: string;
    payload: {
      title?: string;
      phone?: string;
      customerID?: string;
      jobID?: string;
      url?: string;
      msg?: string;
    };
  };
};

export type UseAiAssistantOptions = {
  enabled?: boolean;
};

export function useAiAssistant({ enabled = true }: UseAiAssistantOptions = {}) {
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [feedbackState, setFeedbackState] = useState<Record<string, boolean>>({});
  const [submittedFeedbackDetails, setSubmittedFeedbackDetails] = useState<Record<string, boolean>>({});


  const [conversation, setConversation] = useState<ChatMessage[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { customerProfile, job } = RootStore;

  const { mutate: askAssistant, isLoading: loading } = useMutation(
    async ({ queryText, suggestionLogId }: { queryText: string; suggestionLogId?: number | null }) => {


      const payload: PostKnowledgebasePayload = {
        query: queryText,
        session_id: sessionId,
        customer_ID: customerProfile?.ID ?? null,
        suggestion_log_id: suggestionLogId ?? null,
      };

      return await postKnowledgebase(payload);
    },
    {
      onSuccess: (data: BedrockApiResponse) => {
        if (data.error) throw new Error(data.error);

        if (data.session_id) {
          setSessionId(data.session_id);
        }

        const extractedSources: Array<{ title: string; url: string }> = [];
        if (data.citations) {
          data.citations.forEach((citation) => {
            if (citation?.title && citation?.url) {
              if (!extractedSources.some((s) => s.url === citation?.url)) {
                extractedSources.push({
                  title: citation.title.length > 40 ? citation.title.substring(0, 40) + '...' : citation.title,
                  url: env.KB_SERVER + '/' + citation.url,
                });
              }
            }
          });
        }

        const processedText = data.answer.replace('n **', '\n\n**');

        const assistantMsg: ChatMessage = {
          id: `ai_${Date.now()}`,
          sender: 'assistant',
          text: processedText,
          sourceLinks: extractedSources,
          log_id: data.log_id,
          action: {
            type: data.next_action?.type,
            payload: {
              title: data.next_action?.payload?.title,
              phone: data.next_action?.payload?.phone,
              customerID: data.next_action?.payload?.customerID,
              jobID: data.next_action?.payload?.jobID,
              url: data.next_action?.payload?.url,
              msg: data.next_action?.payload?.msg,
            },
          },
        };
        setConversation((prev) => [...prev, assistantMsg]);
      },
      onError: (err: any) => {

        Toast.show({ description: parseErrors(err).join(', '), type: Toast.Type.Error });

        setErrorMsg(err.message || 'An unexpected error occurred while communicating with the AI.');
      },
    },
  );

  const { mutate: postFeedback } = useMutation(
    async ({ logId, isHelpful, comment }: { logId: string; isHelpful: boolean; comment?: string }) => {
      const payload = {
        log_id: logId,
        is_helpful: isHelpful,
        comment,
      };
      return await rateKnowledgebase(payload);
    },
    {
      onError: (err: any) => {
        console.error('Failed to submit feedback', err);
      },
    },
  );

  const handleFeedback = (logId: string, isHelpful: boolean) => {
    setFeedbackState((prev) => ({ ...prev, [logId]: isHelpful }));

    postFeedback({ logId, isHelpful });
  };

  const handleSubmitFeedbackDetails = (logId: string, comment: string) => {
    setSubmittedFeedbackDetails((prev) => ({ ...prev, [logId]: true }));
    postFeedback({ logId, isHelpful: false, comment });
  };


  const handlePostQuery = (queryText: string, suggestionLogId?: number | null) => {
    const textToSubmit = queryText.trim();
    if (!textToSubmit) return;

    setErrorMsg(null);
    const userMsg: ChatMessage = { id: `user_${Date.now()}`, sender: 'user', text: textToSubmit, log_id: '' };
    setConversation((prev) => [...prev, userMsg]);

    askAssistant({ queryText: textToSubmit, suggestionLogId });
  };


  const handleClearChat = () => {
    setConversation([]);
    setSessionId(null);

    setErrorMsg(null);
  };

  const { suggestions, suggestionsLogId, isLoading: loadingSuggestions } = useSuggestions({
    sessionId,
    conversationLength: conversation.length,
    enabled,
  });

  return {
    loading,
    conversation,
    errorMsg,
    customerProfile,
    job,
    feedbackState,
    submittedFeedbackDetails,
    suggestions,
    suggestionsLogId,
    loadingSuggestions,
    handlePostQuery,
    handleClearChat,
    handleFeedback,
    handleSubmitFeedbackDetails
  };
}
